import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { RespiratoryFormField } from './formField'

/**
 * Respiratory subsection adapter
 * Handles respiratory assessment, cough, oxygen, and lung sounds (5 fields)
 */
export class RespiratoryAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Respiratory assessment fields
    const respiratoryStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL
    )?.at(0)

    const cough = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH
    )?.at(0)

    const oxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN
    )?.at(0)

    const lungSounds = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS
    )

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RespiratoryFormField.RESPIRATORY_RESPIRATORY_WNL]: respiratoryStatus,
      [RespiratoryFormField.RESPIRATORY_COUGH]: cough,
      [RespiratoryFormField.RESPIRATORY_OXYGEN]: oxygen,
      [RespiratoryFormField.RESPIRATORY_LUNG_SOUNDS]: lungSounds,
      [RespiratoryFormField.RESPIRATORY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_RESPIRATORY_WNL,
        value: this.getFormDataField(RespiratoryFormField.RESPIRATORY_RESPIRATORY_WNL)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_OXYGEN,
        value: this.getFormDataField(RespiratoryFormField.RESPIRATORY_OXYGEN)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_LUNG_SOUNDS,
        value: this.getFormDataField(RespiratoryFormField.RESPIRATORY_LUNG_SOUNDS)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_SYSTEM_SUMMARY,
        value: this.getFormDataField(RespiratoryFormField.RESPIRATORY_SYSTEM_SUMMARY)
      }
    ]

    if (this.formDataFieldExists(RespiratoryFormField.RESPIRATORY_COUGH)) {
      mutations.push({
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_RESPIRATORY_COUGH,
        value: this.getFormDataField(RespiratoryFormField.RESPIRATORY_COUGH)
      })
    }

    return mutations
  }
}
